"use client";

import React, { useState } from "react";
import { SlidersHorizontal, CircleCheck, Info, Layers } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type Lang } from "@/lib/i18n";
interface ParameterItem {
  id: string;
  name: Record<Lang, string>;
  symbol: string;
  unit: Record<Lang, string>;
  range: Record<Lang, string>;
  description: Record<Lang, string>;
  formula: string;
  standard: string;
}
const parameters: ParameterItem[] = [{
  id: "mesh-count",
  name: {
    en: "Mesh Count",
    zh: "目数 (Mesh Count)",
    es: "Número de mallas",
    de: "Maschenzahl",
    fr: "Nombre de mailles",
    pt: "Contagem de malhas"
  },
  symbol: "M",
  unit: {
    en: "wires / linear inch (25.4 mm)",
    zh: "每英寸丝数 (25.4 mm)",
    es: "alambres / pulgada lineal (25,4 mm)",
    de: "Drähte / linearer Zoll (25,4 mm)",
    fr: "fils / pouce linéaire (25,4 mm)",
    pt: "fios / polegada linear (25,4 mm)"
  },
  range: {
    en: "1 to 635 Mesh (Square) / Up to 400x2800 (Dutch)",
    zh: "1 至 635 目 (方孔) / 最高 400x2800 (密纹)",
    es: "1 a 635 Mesh (cuadrada) / hasta 400x2800 (Dutch)",
    de: "1 bis 635 Mesh (Quadrat) / bis zu 400x2800 (Tresse)",
    fr: "1 à 635 Mesh (carré) / jusqu'à 400x2800 (hollandais)",
    pt: "1 a 635 Mesh (quadrada) / até 400x2800 (holandesa)"
  },
  description: {
    en: "The number of openings or wires per linear inch measured from the center of any wire.",
    zh: "从任一金属丝中心量起，每英寸线性距离内的孔数或金属丝根数。",
    es: "El número de aberturas o alambres por pulgada lineal medido desde el centro de cualquier alambre.",
    de: "Die Anzahl der Öffnungen oder Drähte pro linearem Zoll, gemessen von der Mitte eines beliebigen Drahtes.",
    fr: "Le nombre d'ouvertures ou de fils par pouce linéaire mesuré depuis le centre d'un fil quelconque.",
    pt: "O número de aberturas ou fios por polegada linear medido a partir do centro de qualquer fio."
  },
  formula: "M = 25.4 / (w + d)",
  standard: "ASTM E2016 / ISO 9044"
}, {
  id: "aperture-size",
  name: {
    en: "Aperture (Mesh Opening)",
    zh: "开孔孔径 (Aperture)",
    es: "Abertura de malla (Luz)",
    de: "Maschenweite (Öffnung)",
    fr: "Ouverture de maille",
    pt: "Abertura da malha (Vão)"
  },
  symbol: "w",
  unit: {
    en: "mm or microns (µm)",
    zh: "毫米 (mm) 或 微米 (µm)",
    es: "mm o micras (µm)",
    de: "mm oder Mikrometer (µm)",
    fr: "mm ou microns (µm)",
    pt: "mm ou micrômetros (µm)"
  },
  range: {
    en: "20 µm to 25.0 mm (Woven range: 1 µm to 25 mm)",
    zh: "20 µm 至 25.0 mm (编织综合 1 µm 至 25 mm)",
    es: "20 µm a 25,0 mm (rango tejido: 1 µm a 25 mm)",
    de: "20 µm bis 25,0 mm (Gewebebereich: 1 µm bis 25 mm)",
    fr: "20 µm à 25,0 mm (gamme tissée : 1 µm à 25 mm)",
    pt: "20 µm a 25,0 mm (faixa tecida: 1 µm a 25 mm)"
  },
  description: {
    en: "The clear distance between two adjacent warp or weft wires defining particle cutoff size.",
    zh: "相邻经丝或纬丝之间的净距离，直接决定颗粒截留截面与过滤精度。",
    es: "La distancia libre entre dos alambres adyacentes de urdimbre o trama que define el corte de partículas.",
    de: "Der lichte Abstand zwischen zwei benachbarten Kett- oder Schussdrähten zur Bestimmung der Partikeltrenngrenze.",
    fr: "La distance libre entre deux fils adjacents de chaîne ou de trame déterminant le seuil de coupure.",
    pt: "A distância livre entre dois fios adjacentes de urdume ou trama que define o ponto de corte de partículas."
  },
  formula: "w = (25.4 / M) - d",
  standard: "ISO 3310-1 / ASTM E11"
}, {
  id: "wire-diameter",
  name: {
    en: "Wire Diameter",
    zh: "金属丝直径 (Wire Diameter)",
    es: "Diámetro de alambre",
    de: "Drahtdurchmesser",
    fr: "Diamètre du fil",
    pt: "Diâmetro do arame"
  },
  symbol: "d",
  unit: {
    en: "mm or inches (SWG/AWG)",
    zh: "毫米 (mm) 或 英寸 (SWG/AWG)",
    es: "mm o pulgadas (SWG/AWG)",
    de: "mm oder Zoll (SWG/AWG)",
    fr: "mm ou pouces (SWG/AWG)",
    pt: "mm ou polegadas (SWG/AWG)"
  },
  range: {
    en: "0.020 mm to 2.0 mm",
    zh: "0.020 mm 至 2.0 mm",
    es: "0,020 mm a 2,0 mm",
    de: "0,020 mm bis 2,0 mm",
    fr: "0,020 mm à 2,0 mm",
    pt: "0,020 mm a 2,0 mm"
  },
  description: {
    en: "The exact nominal gauge of the wire before weaving, directly impacting mechanical strength.",
    zh: "编织前金属丝的精准公称线径，直接影响丝网的机械强度与耐压等级。",
    es: "El calibre nominal exacto del alambre antes del tejido, que determina la resistencia mecánica.",
    de: "Der exakte Nenndurchmesser des Drahtes vor dem Weben, der die mechanische Festigkeit bestimmt.",
    fr: "Le diamètre nominal exact du fil avant tissage, déterminant directement la résistance mécanique.",
    pt: "A bitola nominal exata do arame antes da tecelagem, influenciando diretamente a resistência mecânica."
  },
  formula: "d = (25.4 / M) - w",
  standard: "DIN 4189 / ASTM E2016"
}, {
  id: "open-area",
  name: {
    en: "Open Area (Screening Area)",
    zh: "开孔率 (Open Area)",
    es: "Área abierta (superficie útil)",
    de: "Offene Siebfläche",
    fr: "Surface ouverte (vide de maille)",
    pt: "Área aberta (superfície útil)"
  },
  symbol: "Ao",
  unit: {
    en: "percentage (%)",
    zh: "百分比 (%)",
    es: "porcentaje (%)",
    de: "Prozentsatz (%)",
    fr: "pourcentage (%)",
    pt: "porcentagem (%)"
  },
  range: {
    en: "18.0% to 75.4%",
    zh: "18.0% 至 75.4%",
    es: "18,0% a 75,4%",
    de: "18,0% bis 75,4%",
    fr: "18,0% à 75,4%",
    pt: "18,0% a 75,4%"
  },
  description: {
    en: "The proportion of open surface area across the total screen face governing flow rate and delta P.",
    zh: "丝网通孔表面积占总表面积的比例，直接影响流体通量与初始压降 (ΔP)。",
    es: "La proporción de área superficial abierta sobre la cara total que gobierna el caudal y la pérdida de carga.",
    de: "Der Anteil der offenen Fläche an der gesamten Sieboberfläche, maßgeblich für Durchfluss und Druckverlust.",
    fr: "La proportion de surface ouverte sur l'ensemble de la toile déterminant le débit et la perte de charge (ΔP).",
    pt: "A proporção de área aberta sobre a superfície total que determina a vazão e a perda de carga (ΔP)."
  },
  formula: "Ao = [w / (w + d)]² × 100",
  standard: "ISO 9044 Calculation"
}, {
  id: "tolerances",
  name: {
    en: "Weaving Tolerances",
    zh: "编织公差等级 (Tolerances)",
    es: "Tolerancias de tejido",
    de: "Webtoleranzen",
    fr: "Tolérances de tissage",
    pt: "Tolerâncias de tecelagem"
  },
  symbol: "±T",
  unit: {
    en: "precision limits (%)",
    zh: "精度极限范围 (%)",
    es: "límites de precisión (%)",
    de: "Präzisionsgrenzen (%)",
    fr: "limites de précision (%)",
    pt: "limites de precisão (%)"
  },
  range: {
    en: "Standard / Tight industrial tolerance",
    zh: "工业标准与紧公差等级",
    es: "Tolerancia industrial estándar / estrecha",
    de: "Standard- und Engtoleranzklassen",
    fr: "Tolérance industrielle standard / resserrée",
    pt: "Tolerância industrial padrão / restrita"
  },
  description: {
    en: "Permissible dimensional deviation for aperture consistency, wire spacing, and mesh thickness.",
    zh: "孔径一致性、丝距分布和网面厚度的允许尺寸公差偏差范围。",
    es: "Desviación dimensional admisible para la uniformidad de la abertura, separación de alambres y espesor.",
    de: "Zulässige Maßabweichungen für Öffnungsgleichmäßigkeit, Drahtabstände und Gewebedicke.",
    fr: "Écart dimensionnel admissible pour la régularité de l'ouverture, l'écartement des fils et l'épaisseur.",
    pt: "Desvio dimensional admissível para uniformidade de abertura, espaçamento dos fios e espessura da tela."
  },
  formula: "Per ASTM E2016 Tabular Standard",
  standard: "ASTM E2016 / ISO 9044"
}];
interface GradeItem {
  title: string;
  detail: Record<Lang, string>;
}
const standardGrades: GradeItem[] = [{
  title: "AISI 304 / 304L",
  detail: {
    en: "Standard general industrial filtration, organic acids, water streams",
    zh: "常规工业通用过滤、有机酸环境、水处理与一般流体分离",
    es: "Filtración industrial general estándar, ácidos orgánicos y corrientes de agua",
    de: "Standard-Industriefiltration, organische Säuren, Wasserströme",
    fr: "Filtration industrielle générale standard, acides organiques et circuits d'eau",
    pt: "Filtração industrial geral padrão, ácidos orgânicos e fluxos de água"
  }
}, {
  title: "AISI 316 / 316L",
  detail: {
    en: "Marine, pharmaceutical, chemical, and chloride-resistant applications",
    zh: "海洋工程、制药、精细化工与耐氯化物腐蚀工况",
    es: "Aplicaciones marinas, farmacéuticas, químicas y resistentes a cloruros",
    de: "Marine-, Pharma-, Chemie- und chloridbeständige Anwendungen",
    fr: "Applications marines, pharmaceutiques, chimiques et résistance aux chlorures",
    pt: "Aplicações marinhas, farmacêuticas, químicas e resistentes a cloretos"
  }
}, {
  title: "AISI 314 / 310S",
  detail: {
    en: "High temperature furnaces, thermal processing applications",
    zh: "工业高温炉体、热处理工艺高温工况",
    es: "Hornos de alta temperatura y aplicaciones de tratamiento térmico",
    de: "Hochtemperaturöfen und thermische Verarbeitungsanwendungen",
    fr: "Fours haute température et procédés thermiques industriels",
    pt: "Fornos de alta temperatura e processos térmicos industriais"
  }
}, {
  title: "Duplex 2205",
  detail: {
    en: "Enhanced mechanical strength and chloride stress corrosion resistance",
    zh: "高机械强度工况与耐氯离子应力腐蚀工况",
    es: "Mayor resistencia mecánica y resistencia a la corrosión bajo tensión por cloruros",
    de: "Erhöhte mechanische Festigkeit und Beständigkeit gegen Chlorid-Spannungsrisskorrosion",
    fr: "Résistance mécanique renforcée et tenue à la corrosion sous contrainte en milieu chloré",
    pt: "Maior resistência mecânica e resistência à corrosão sob tensão por cloretos"
  }
}];
interface CopyContent {
  sectionHeading: string;
  sectionLead: string;
  selectParamLabel: string;
  brandSpecLabel: string;
  unitLabel: string;
  rangeLabel: string;
  governingLabel: string;
  tooltipInfo: string;
  formulaNote: string;
  verificationTitle: string;
  checklist: string[];
  customAlloyPrompt: string;
  viewMeshTable: string;
  materialsTitle: string;
  materialsSubtitle: string;
  viewMaterialGuide: string;
}
const copyData: Record<Lang, CopyContent> = {
  en: {
    sectionHeading: "Geometric Variables & Governing Formulations",
    sectionLead: "Under ASTM E2016 and ISO 9044 standards, mesh count, aperture size, wire diameter, and open area form a unified mathematical relationship.",
    selectParamLabel: "Select Parameter",
    brandSpecLabel: "MutualFilter Engineering Spec",
    unitLabel: "Measurement Unit",
    rangeLabel: "Production Range",
    governingLabel: "Governing Formulation",
    tooltipInfo: "Mathematical correlation between aperture (w), wire diameter (d), and mesh count (M).",
    formulaNote: "Calculated in accordance with ASTM E2016 standard specification for industrial woven wire filter cloth.",
    verificationTitle: "Inspection & Quality Verification",
    checklist: ["Optical automated aperture measurement", "Laser micrometry wire verification", "Material chemical analysis & PMI (available on request)", "Roll tension and surface flatness inspection"],
    customAlloyPrompt: "Custom weave pattern or aperture required?",
    viewMeshTable: "View Mesh Table",
    materialsTitle: "Common Stainless Steel Material Grades",
    materialsSubtitle: "Austenitic and duplex stainless steel alloys suited for corrosive, sanitary, or thermal operational environments.",
    viewMaterialGuide: "View Material Selection Guide"
  },
  zh: {
    sectionHeading: "工业丝网几何与力学计算参数",
    sectionLead: "依据 ASTM E2016 与 ISO 9044 工业丝网标准，目数、孔径、丝径与开孔率之间存在严格的数学关联。",
    selectParamLabel: "选择技术参数",
    brandSpecLabel: "MutualFilter 工程规范",
    unitLabel: "计量单位",
    rangeLabel: "生产范围",
    governingLabel: "控制计算公式",
    tooltipInfo: "孔径 (w)、丝径 (d) 与目数 (M) 之间的严格数学关联关系。",
    formulaNote: "依据工业编织金属丝过滤网布 ASTM E2016 标准规范计算。",
    verificationTitle: "检验与质量验证标准",
    checklist: ["光学自动化孔径精密测量", "激光测微计丝径校准验证", "材质成分分析与理化检测（可按订单要求提供报告）", "网卷张力与表面平整度检测"],
    customAlloyPrompt: "需要特殊编织结构或规格参数？",
    viewMeshTable: "查看丝网参数表",
    materialsTitle: "常用不锈钢材质牌号",
    materialsSubtitle: "高抗拉强度奥氏体与双相不锈钢合金，适配腐蚀性流体、卫生级或高温工业工况。",
    viewMaterialGuide: "查看材质选型指南"
  },
  es: {
    sectionHeading: "Variables geométricas y fórmulas de cálculo",
    sectionLead: "Bajo las normas ASTM E2016 e ISO 9044, el número de mallas, la abertura, el diámetro del alambre y el área abierta mantienen una relación matemática unificada.",
    selectParamLabel: "Seleccionar parámetro",
    brandSpecLabel: "Especificación técnica MutualFilter",
    unitLabel: "Unidad de medida",
    rangeLabel: "Gama de producción",
    governingLabel: "Fórmula de cálculo determinante",
    tooltipInfo: "Relación matemática directa entre abertura (w), diámetro de alambre (d) y número de mallas (M).",
    formulaNote: "Calculado según la norma ASTM E2016 para telas filtrantes de alambre tejido industrial.",
    verificationTitle: "Inspección y verificación de calidad",
    checklist: ["Medición óptica automatizada de abertura", "Verificación micrométrica láser del alambre", "Análisis químico de material y PMI (disponible según pedido)", "Inspección de tensión del rollo y planitud superficial"],
    customAlloyPrompt: "¿Requiere un tejido especial o parámetros a medida?",
    viewMeshTable: "Ver tabla de mallas",
    materialsTitle: "Grados de acero inoxidable comunes",
    materialsSubtitle: "Aleaciones de acero inoxidable austenítico y dúplex adaptadas a entornos corrosivos, sanitarios o de alta temperatura.",
    viewMaterialGuide: "Ver guía de selección de materiales"
  },
  de: {
    sectionHeading: "Geometrische Variablen und Berechnungsgrundlagen",
    sectionLead: "Gemäß ASTM E2016 und ISO 9044 stehen Maschenzahl, Maschenweite, Drahtdurchmesser und offene Siebfläche in einem festen mathematischen Zusammenhang.",
    selectParamLabel: "Parameter auswählen",
    brandSpecLabel: "MutualFilter Konstruktionsnorm",
    unitLabel: "Maßeinheit",
    rangeLabel: "Fertigungsbereich",
    governingLabel: "Maßgebliche Berechnungsformel",
    tooltipInfo: "Mathematischer Zusammenhang zwischen Maschenweite (w), Drahtdurchmesser (d) und Maschenzahl (M).",
    formulaNote: "Berechnet nach ASTM E2016 Standardspezifikation für Industriedrahtgewebe.",
    verificationTitle: "Prüfung & Qualitätsverifikation",
    checklist: ["Optische automatisierte Maschenweitenmessung", "Laser-Mikrometer-Drahtprüfung", "Werkstoff-Chemische Analyse & PMI (auf Anfrage auftragsbezogen)", "Prüfung von Rollenspannung und Oberflächenebenheit"],
    customAlloyPrompt: "Sondergewebe oder spezifische Abmessungen erforderlich?",
    viewMeshTable: "Gewebetabelle anzeigen",
    materialsTitle: "Gängige Edelstahl-Werkstoffgüten",
    materialsSubtitle: "Austenitische und Duplex-Edelstähle für korrosive, hygienische oder thermisch anspruchsvolle Betriebsumgebungen.",
    viewMaterialGuide: "Werkstoff-Leitfaden anzeigen"
  },
  fr: {
    sectionHeading: "Variables géométriques et formulations de calcul",
    sectionLead: "Selon les normes ASTM E2016 et ISO 9044, le nombre de mailles, l'ouverture, le diamètre de fil et le vide de maille forment une relation mathématique unifiée.",
    selectParamLabel: "Sélectionner un paramètre",
    brandSpecLabel: "Spécification technique MutualFilter",
    unitLabel: "Unité de mesure",
    rangeLabel: "Plage de fabrication",
    governingLabel: "Formule de calcul déterminante",
    tooltipInfo: "Corrélation mathématique entre ouverture (w), diamètre de fil (d) et nombre de mailles (M).",
    formulaNote: "Calculé selon la norme ASTM E2016 pour toiles filtrantes industrielles en fils tissés.",
    verificationTitle: "Contrôle & vérification de la qualité",
    checklist: ["Mesure optique automatisée de l'ouverture", "Vérification laser du diamètre de fil", "Analyse chimique et CND/PMI (disponible sur demande selon commande)", "Contrôle de tension du rouleau et planéité de surface"],
    customAlloyPrompt: "Structure de tissage ou paramètres sur mesure nécessaires ?",
    viewMeshTable: "Voir le tableau des toiles",
    materialsTitle: "Nuances d'acier inoxydable courantes",
    materialsSubtitle: "Aciers inoxydables austénitiques et duplex adaptés aux environnements corrosifs, sanitaires ou thermiques.",
    viewMaterialGuide: "Voir le guide des matériaux"
  },
  pt: {
    sectionHeading: "Variáveis geométricas e formulações de cálculo",
    sectionLead: "Conforme as normas ASTM E2016 e ISO 9044, a contagem de malha, a abertura, o diâmetro do arame e a área aberta formam uma relação matemática unificada.",
    selectParamLabel: "Selecionar parâmetro",
    brandSpecLabel: "Especificação de engenharia MutualFilter",
    unitLabel: "Unidade de medida",
    rangeLabel: "Faixa de produção",
    governingLabel: "Fórmula de cálculo determinante",
    tooltipInfo: "Correlação matemática entre abertura (w), diâmetro do arame (d) e contagem de malha (M).",
    formulaNote: "Calculado conforme a norma ASTM E2016 para telas industriais de arame tecido.",
    verificationTitle: "Inspeção e controle de qualidade",
    checklist: ["Medição óptica automatizada da abertura", "Verificação micrométrica a laser do arame", "Análise química do material e PMI (disponível sob solicitação)", "Inspeção de tensão do rolo e nivelamento da superfície"],
    customAlloyPrompt: "Necessita de tecelagem especial ou abertura sob medida?",
    viewMeshTable: "Ver tabela de malhas",
    materialsTitle: "Classes comuns de aço inoxidável",
    materialsSubtitle: "Ligas de aço inoxidável austenítico e duplex adequadas para ambientes corrosivos, sanitários ou térmicos.",
    viewMaterialGuide: "Ver guia de seleção de materiais"
  }
};
export default function Specifications() {
  const [selectedParamId, setSelectedParamId] = useState<string>("mesh-count");
  const {
    lang
  } = useLanguage();
  const content = pick(lang, copyData);
  const activeParam = parameters.find(p => p.id === selectedParamId) || parameters[0];
  return <section id="specificationparameters" data-nav="light" className="w-full bg-background text-foreground mf-section border-b border-border">
      <div className="mf-container">
        {/* Section Heading */}
        <div className="mb-10 max-w-3xl">
          <h2 className="mf-h2 font-default text-foreground">
            {content.sectionHeading}
          </h2>
          <p className="mt-3 text-muted-foreground mf-lead">
            {content.sectionLead}
          </p>
        </div>

        {/* Interactive Parameter Console: Selector Column + Detail Card */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-start mb-16">
          {/* Parameter Selectors (5 cols on lg) */}
          <div className="lg:col-span-5 flex flex-col gap-3">
            <div className="flex items-center justify-between pb-2 border-b border-border">
              <span className="mf-caption font-mono font-semibold text-muted-foreground uppercase tracking-wider">
                {content.selectParamLabel}
              </span>
              <span className="mf-caption font-mono text-muted-foreground">
                ISO 9044 / ASTM E2016
              </span>
            </div>

            {parameters.map(item => {
            const isSelected = item.id === selectedParamId;
            const itemName = pick(lang, item.name);
            const itemDesc = pick(lang, item.description);
            const itemRange = pick(lang, item.range);
            return <div key={item.id} onClick={() => setSelectedParamId(item.id)} className={`cursor-pointer rounded-lg p-4 border text-left transition-[background-color,border-color] ${isSelected ? "bg-card border-primary ring-1 ring-primary" : "bg-background border-border hover:bg-muted/50 hover:border-border/80"}`} role="button" tabIndex={0} onKeyDown={e => {
              if (e.key === "Enter" || e.key === " ") {
                setSelectedParamId(item.id);
              }
            }} aria-pressed={isSelected}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <span className="font-mono mf-caption font-semibold px-2 py-0.5 rounded bg-muted text-primary border border-border">
                        {item.symbol}
                      </span>
                      <span className="font-default font-semibold text-sm sm:text-base text-foreground">
                        {itemName}
                      </span>
                    </div>
                    <span className="font-mono mf-caption text-muted-foreground">
                      {itemRange.split(" ")[0]}
                    </span>
                  </div>
                  <p className="mf-caption text-muted-foreground line-clamp-1 mt-1">
                    {itemDesc}
                  </p>
                </div>;
          })}
          </div>

          {/* Parameter Engineering Detail Card (7 cols on lg) */}
          <div className="lg:col-span-7">
            <Card className="rounded-lg border border-border bg-card relative overflow-hidden">
              <CardHeader className="border-b border-border pb-5">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="font-mono mf-caption px-2.5 py-0.5 bg-muted text-foreground">
                      {`Symbol: ${activeParam.symbol}`}
                    </Badge>
                    <Badge variant="outline" className="font-mono mf-caption bg-muted text-foreground">
                      {activeParam.standard}
                    </Badge>
                  </div>
                  <span className="mf-caption font-mono text-muted-foreground">
                    {content.brandSpecLabel}
                  </span>
                </div>
                <CardTitle className="mf-h3 font-bold font-default text-foreground mt-2">
                  {pick(lang, activeParam.name)}
                </CardTitle>
                <CardDescription className="text-sm text-muted-foreground font-default">
                  {pick(lang, activeParam.description)}
                </CardDescription>
              </CardHeader>

              <CardContent className="pt-6 space-y-6">
                {/* Engineering Data Points */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="rounded-lg p-4 bg-muted/40 border border-border/70">
                    <span className="mf-caption font-mono text-muted-foreground uppercase tracking-wider block mb-1">
                      {content.unitLabel}
                    </span>
                    <span className="text-sm font-semibold font-default text-foreground">
                      {pick(lang, activeParam.unit)}
                    </span>
                  </div>

                  <div className="rounded-lg p-4 bg-muted/40 border border-border/70">
                    <span className="mf-caption font-mono text-muted-foreground uppercase tracking-wider block mb-1">
                      {content.rangeLabel}
                    </span>
                    <span className="text-sm font-semibold font-default text-foreground">
                      {pick(lang, activeParam.range)}
                    </span>
                  </div>
                </div>

                {/* Mathematical Relationship Formula */}
                <div className="rounded-lg p-4 bg-muted/30 border border-border">
                  <div className="flex items-center justify-between mb-2">
                    <span className="mf-caption font-mono font-medium text-foreground uppercase tracking-wider flex items-center gap-1.5">
                      <SlidersHorizontal className="size-3.5 text-primary" aria-hidden="true" />
                      {content.governingLabel}
                    </span>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="cursor-help text-muted-foreground hover:text-foreground">
                            <Info className="size-3.5" aria-hidden="true" />
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>
                          {content.tooltipInfo}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <div className="font-mono text-base sm:text-lg font-semibold text-primary py-2 px-3 bg-background rounded-lg border border-border">
                    {activeParam.formula}
                  </div>
                  <p className="mf-caption text-muted-foreground mt-2">
                    {content.formulaNote}
                  </p>
                </div>

                {/* Quality Verification Checklist */}
                <div>
                  <h3 className="mf-caption font-mono uppercase tracking-wider font-semibold text-muted-foreground mb-3 flex items-center gap-1.5">
                    <CircleCheck className="size-4 text-primary" aria-hidden="true" />
                    {content.verificationTitle}
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mf-caption text-foreground font-default">
                    {content.checklist.map((item, idx) => <div key={idx} className="flex items-center gap-2 p-2 rounded-lg bg-background border border-border">
                        <div className="size-1.5 rounded-full bg-primary shrink-0" />
                        <span>{item}</span>
                      </div>)}
                  </div>
                </div>

                <div className="pt-2 flex flex-wrap items-center justify-between gap-3 border-t border-border">
                  <span className="mf-caption text-muted-foreground">
                    {content.customAlloyPrompt}
                  </span>
                  <Button size="sm" variant="outline" asChild>
                    <Link to="/MeshCountAndAperturePage">
                      {content.viewMeshTable}
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Industrial Grade Matrix Banner */}
        <div className="rounded-lg border border-border bg-muted/30 p-6 md:p-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Layers className="size-4 text-primary" aria-hidden="true" />
                <h3 className="font-default font-bold mf-h3 text-foreground">
                  {content.materialsTitle}
                </h3>
              </div>
              <p className="text-sm text-muted-foreground font-default">
                {content.materialsSubtitle}
              </p>
            </div>
            <Button variant="outline" size="sm" asChild>
              <Link to="/MaterialsAndSurfaceFinishesPage">{content.viewMaterialGuide}</Link>
            </Button>
          </div>

          <Separator className="mb-6" />

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {standardGrades.map(grade => <div key={grade.title} className="rounded-lg p-4 bg-card border border-border/80 hover:border-primary/50 transition-colors">
                <div className="font-mono text-sm font-semibold text-primary mb-1">
                  {grade.title}
                </div>
                <p className="mf-caption text-muted-foreground font-default leading-relaxed">
                  {pick(lang, grade.detail)}
                </p>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}